import React from 'react';
import {
  Box,
  Container,
  Grid,
  Stack,
  Typography,
  Paper,
  TextField,
  Button,
  MenuItem,
  Link,
  Alert,
} from '@mui/material';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';

import SectionHeading from '../components/SectionHeading';
import { brand } from '../theme';

const TOPICS = [
  { value: 'concierge', label: 'I want a Concierge demo' },
  { value: 'custom', label: 'I want to talk about a custom build' },
  { value: 'partnership', label: 'Partnership / referral' },
  { value: 'other', label: 'Something else' },
];

const CONTACT_EMAIL = 'craig@gnrnexus.com';

export default function Contact() {
  const [form, setForm] = React.useState({
    name: '',
    company: '',
    email: '',
    topic: 'concierge',
    message: '',
  });
  const [sent, setSent] = React.useState(false);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(
      `[G&R Nexus] ${TOPICS.find((t) => t.value === form.topic)?.label || 'Inquiry'} — ${form.company || form.name || 'New inquiry'}`,
    );
    const bodyLines = [
      `Name: ${form.name}`,
      `Company: ${form.company}`,
      `Email: ${form.email}`,
      `Topic: ${TOPICS.find((t) => t.value === form.topic)?.label || form.topic}`,
      '',
      'Message:',
      form.message,
    ];
    const body = encodeURIComponent(bodyLines.join('\n'));
    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
    setSent(true);
  };

  return (
    <Box>
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          bgcolor: brand.navy,
          color: '#fff',
          pt: { xs: 10, md: 14 },
          pb: { xs: 8, md: 10 },
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(900px 460px at 90% -10%, rgba(30,91,255,0.55), transparent 60%),' +
              'radial-gradient(640px 320px at -10% 100%, rgba(201,161,74,0.20), transparent 65%)',
          }}
        />
        <Container maxWidth="md" sx={{ position: 'relative', textAlign: 'center' }}>
          <Typography
            variant="overline"
            sx={{ color: brand.blueLight, letterSpacing: '0.18em', fontWeight: 700 }}
          >
            Contact
          </Typography>
          <Typography
            variant="h1"
            sx={{ fontSize: { xs: '2.2rem', md: '3.2rem' }, color: '#fff', mt: 1.5, mb: 2 }}
          >
            Let’s talk about what you’re building.
          </Typography>
          <Typography variant="body1" sx={{ color: '#CBD5E1', maxWidth: 620, mx: 'auto' }}>
            Drop us a note and we’ll respond within one business day. For Concierge demos we’ll
            usually book a 20-minute call.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Grid container spacing={{ xs: 5, md: 8 }}>
          <Grid size={{ xs: 12, md: 5 }}>
            <SectionHeading
              eyebrow="Get in touch"
              title="A few ways to reach us."
              subtitle="Email is fastest. We aim to respond within one business day."
              align="left"
              maxWidth={520}
            />
            <Stack spacing={2.5}>
              <ContactRow
                icon={EmailRoundedIcon}
                label="Email"
                value={
                  <Link href={`mailto:${CONTACT_EMAIL}`} underline="hover" color="inherit">
                    {CONTACT_EMAIL}
                  </Link>
                }
              />
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, md: 7 }}>
            <Paper
              elevation={0}
              component="form"
              onSubmit={handleSubmit}
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 4,
                border: `1px solid ${brand.border}`,
                boxShadow: '0 24px 60px -30px rgba(15,23,42,0.18)',
              }}
            >
              <Typography variant="h4" sx={{ mb: 0.5, color: brand.navy }}>
                Tell us about your business
              </Typography>
              <Typography variant="body2" sx={{ color: brand.slate, mb: 3 }}>
                We’ll open your email client with a draft pre-filled — review it and hit send.
              </Typography>

              {sent && (
                <Alert severity="success" sx={{ mb: 3 }}>
                  Your email draft should have opened. If not, reach out directly at{' '}
                  <Link href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Link>.
                </Alert>
              )}

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Your name"
                    fullWidth
                    required
                    value={form.name}
                    onChange={handleChange('name')}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Company"
                    fullWidth
                    value={form.company}
                    onChange={handleChange('company')}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label="Email"
                    type="email"
                    fullWidth
                    required
                    value={form.email}
                    onChange={handleChange('email')}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    select
                    label="What can we help with?"
                    fullWidth
                    value={form.topic}
                    onChange={handleChange('topic')}
                  >
                    {TOPICS.map((t) => (
                      <MenuItem key={t.value} value={t.value}>
                        {t.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label="Message"
                    multiline
                    minRows={4}
                    fullWidth
                    required
                    value={form.message}
                    onChange={handleChange('message')}
                    placeholder="A few lines about your business and what you’re trying to fix."
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="large"
                    endIcon={<SendRoundedIcon />}
                    sx={{ mt: 1 }}
                  >
                    Send via email
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

function ContactRow({ icon: Icon, label, value }) {
  return (
    <Stack direction="row" spacing={2} alignItems="flex-start">
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 1.5,
          bgcolor: 'rgba(30,91,255,0.10)',
          color: brand.blue,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon />
      </Box>
      <Box>
        <Typography variant="overline" sx={{ color: brand.slate, letterSpacing: '0.14em', fontWeight: 700 }}>
          {label}
        </Typography>
        <Typography variant="body1" sx={{ color: brand.navy, fontWeight: 500 }}>
          {value}
        </Typography>
      </Box>
    </Stack>
  );
}
